import React, { useState } from 'react';
import { ArrowRight, FileDown, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { mockData } from '../mock';
import { useT } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';
import { EditButton } from './admin/EditorShell';
import { SchemaEditor } from './admin/SchemaEditor';
import { pictureUrl } from '../lib/api';

const DOWNLOAD_SCHEMA = [
  { key: 'file', label: 'PDF-fil (last opp eller lim inn lenke)', type: 'file' },
  { key: 'label', label: 'Knappetekst' },
  { key: 'sub', label: 'Liten tekst over' },
  { key: 'hidden', label: 'Skjul knappen', type: 'checkbox', hint: 'Skjul «Last ned programmet» heilt' },
];
const PROGRAM_SCHEMA = [
  { key: 'day', label: 'Dag (t.d. FREDAG)' },
  { key: 'date', label: 'Dato (t.d. 2. APRIL)' },
  { key: 'items', label: 'Programpunkt', type: 'list', itemLabel: 'punkt', schema: [
    { key: 'time', label: 'Klokkeslett' },
    { key: 'title', label: 'Tittel' },
    { key: 'description', label: 'Skildring', type: 'textarea', rows: 3 },
    { key: 'image', label: 'Bilete', type: 'image' },
  ] },
];

const Program = () => {
  const { t } = useT();
  const [active, setActive] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editingDl, setEditingDl] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { content } = useContent();
  const dl = { file: mockData.programPdf, label: 'Last ned programmet', sub: 'PDF · Heile helga', hidden: false, ...(content.programDownload || {}) };
  const dlHref = /^\/api\//.test(dl.file) ? `${process.env.REACT_APP_BACKEND_URL}${dl.file}` : dl.file;
  const program = content.program?.length ? content.program : mockData.program;
  const day = program[Math.min(active, program.length - 1)];

  return (
    <section id="program" className="bg-black py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-start justify-between gap-4 mb-8 md:mb-12">
          <h2 className="text-5xl md:text-8xl font-bold text-white tracking-tighter uppercase" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
            {t('Program')}
          </h2>
          <EditButton onClick={() => setEditing(true)} testId="edit-program-button" />
        </div>

        <div className="max-w-3xl mx-auto">
          <div role="tablist" aria-label="Program" style={{ gridTemplateColumns: `repeat(${program.length}, minmax(0, 1fr))` }} className="grid gap-2 md:gap-3 p-1.5 rounded-full bg-[#1a1424] border border-white/5 mb-6 md:mb-8" data-testid="program-tabs">
            {program.map((d, i) => (
              <button
                key={d.day}
                type="button"
                role="tab"
                aria-selected={i === active}
                onClick={() => setActive(i)}
                data-testid={`program-tab-${d.day.toLowerCase()}`}
                className={`rounded-full py-2.5 md:py-3.5 text-center transition-colors ${i === active ? 'bg-[#ff2e2e] text-black' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                <span className="block text-sm md:text-lg font-bold uppercase tracking-wide" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(d.day)}</span>
                <span className={`block text-[10px] md:text-xs tracking-wider uppercase ${i === active ? 'text-black/70' : 'text-white/40'}`}>{t(d.date)}</span>
              </button>
            ))}
          </div>

          <div key={day.day} data-testid={`program-day-${day.day.toLowerCase()}`} className="rounded-3xl md:rounded-[2rem] bg-[#1f1633] border border-white/5 p-4 md:p-8 animate-[fadeUp_0.35s_ease-out]">
            <ul className="divide-y divide-white/5">
              {day.items.map((item, i) => (
                <li key={i} className="flex items-start gap-4 md:gap-6 py-3.5 md:py-4 group" data-testid={`program-item-${day.day.toLowerCase()}-${i}`}>
                  <span className="shrink-0 w-[4.5rem] md:w-24 inline-flex items-center gap-1.5 text-[#ff2e2e] text-base md:text-xl font-bold tabular-nums pt-0.5">
                    <Clock size={14} className="hidden md:block opacity-70" /> {item.time}
                  </span>
                  <button type="button" onClick={() => setSelectedItem(item)} aria-label={`${t(item.title)} – ${t('Sjå detaljar')}`} className="flex flex-1 min-w-0 items-start justify-between gap-3 text-left">
                    <span className="min-w-0 text-white text-base md:text-xl leading-snug break-words group-hover:text-[#ff2e2e] transition-colors">
                      {t(item.title)}
                    </span>
                    <ArrowRight size={18} strokeWidth={2.5} className="shrink-0 mt-1 text-white/30 group-hover:text-[#ff2e2e] group-hover:translate-x-1 transition-[color,transform]" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 mt-12">
          <EditButton onClick={() => setEditingDl(true)} label="Rediger nedlasting" testId="edit-download-button" />
          {!dl.hidden && <a
            href={dlHref}
            download="Program_Refuel_Weekend_27.pdf"
            data-testid="program-download-link"
            className="group relative flex items-center gap-4 pl-3 pr-7 py-3 rounded-full bg-[#ff2e2e] text-black shadow-[0_0_40px_rgba(255,46,46,0.45)] hover:shadow-[0_0_60px_rgba(255,46,46,0.7)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
          >
            <span className="absolute inset-0 rounded-full ring-2 ring-[#ff2e2e]/50 animate-ping opacity-40 pointer-events-none" />
            <span className="relative w-14 h-14 rounded-full bg-black text-[#ff2e2e] flex items-center justify-center shrink-0 group-hover:bg-white group-hover:text-black transition-colors">
              <FileDown size={26} strokeWidth={2.2} className="group-hover:translate-y-0.5 transition-transform" />
            </span>
            <span className="relative text-left leading-tight">
              <span className="block text-[10px] uppercase tracking-[0.25em] font-bold text-black/60">{t(dl.sub)}</span>
              <span className="block text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(dl.label)}</span>
            </span>
            <ArrowRight size={22} strokeWidth={2.5} className="relative ml-2 group-hover:translate-x-1 transition-transform" />
          </a>}
        </div>
      </div>
      {editingDl && <SchemaEditor contentKey="programDownload" title="Rediger «Last ned programmet»" single initial={dl} schema={DOWNLOAD_SCHEMA} onClose={() => setEditingDl(false)} />}
      {editing && <SchemaEditor contentKey="program" title="Rediger programmet" initial={program.map((d) => ({ id: d.day, ...d }))} schema={PROGRAM_SCHEMA} itemLabel="dag" onClose={() => setEditing(false)} />}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent data-testid="program-item-dialog" className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90dvh] overflow-y-auto p-0 bg-[#0f0a17] border-white/10 text-white rounded-2xl [&>button]:text-white">
          {selectedItem && (
            <div>
              {selectedItem.image && <img src={pictureUrl(selectedItem.image)} alt={t(selectedItem.title)} className="w-full max-h-[55vh] object-cover" />}
              <div className="p-5 sm:p-8">
                <p className="text-[#ff2e2e] text-sm font-bold uppercase">{selectedItem.time}</p>
                <DialogTitle className="mt-2 text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(selectedItem.title)}</DialogTitle>
                <DialogDescription className="mt-4 whitespace-pre-line text-white/75 leading-relaxed">
                  {selectedItem.description ? t(selectedItem.description) : t('Meir informasjon kjem snart.')}
                </DialogDescription>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Program;
